import 'element-closest';
import 'whatwg-fetch';
import CustomEvent from 'custom-event';
import serialize from 'form-serialize';
import { Loader } from 'google-maps';
import { forEach } from '../../../includes/js/helpers';

// Initialize dataLayer.
window.dataLayer = window.dataLayer || [];

/**
 * Object containing all Locator functionality.
 *
 * @since 1.0.0
 *
 * @type {Object}
 */
export default class EmLocator {
	/**
	 * Plugin settings.
	 * @type {Object}
	 */
	settings = { ...window.emLocatorSettings };

	/**
	 * Placeholder for all active map markers.
	 * @type {Array}
	 */
	markers = [];

	/**
	 * Placeholder for all active map infowindows.
	 * @type {Array}
	 */
	infowindows = [];

	activeInfowindow = false;

	hoverInfowindow = null;

	hoverCloseTimeout = null;

	/**
	 * Track the search input's previous value for comparison.
	 */
	currentSearchValue = '';

	/**
	 * Keep track of the current lat/lng coordinates.
	 */
	currentPosition = {
		lat: false,
		lng: false,
	};

	cluster = false;

	/**
	 * Keep track of pagination variables returned from REST Response.
	 */
	pagination = {
		totalPosts: 0,
		totalPages: 0,
		currentPage: 0,
		perPage: parseInt(window.emLocatorSettings.postsPerPage),
	};

	markerIcon = {
		url: '/wp-content/plugins/aquablation-physician-locator/public/images/map-marker.svg',
	};

	markerIconHover = {
		url: '/wp-content/plugins/aquablation-physician-locator/public/images/map-marker-hover.svg',
	};

	/**
	 * Initialize all Locator functionality.
	 *
	 * @since 1.0.0
	 */
	constructor(locatorEl) {
		this.locatorEl = locatorEl;
		this.getGlobalElements();
		this.initMapLayout();
		this.initListLayout();
		this.initBasicAuth();
		this.initPagination();
		this.initGoogleMaps();
		this.initSearchStickyOffset();
	}

	initSearchStickyOffset = () => {
		const siteHeader = document.querySelector('.site-header');
		const emlSearch = this.locatorEl.querySelector('.eml-search');
		const mapContainer = this.locatorEl.querySelector('.js-eml-map-container');
		const mobileSearch = mapContainer
			? mapContainer.querySelector('.eml-search')
			: null;

		if (!siteHeader || !emlSearch) {
			return;
		}

		const update = () => {
			const headerBottom = siteHeader.getBoundingClientRect().bottom;
			emlSearch.style.top = `${headerBottom}px`;
			const searchBottom = headerBottom + emlSearch.offsetHeight;
			this.locatorEl.style.setProperty(
				'--eml-search-bottom',
				`${searchBottom}px`,
			);

			if (mobileSearch) {
				const mobileSearchTop =					parseFloat(getComputedStyle(mobileSearch).top) || 0;
				const mobileSearchBottom = mobileSearchTop + mobileSearch.offsetHeight;
				this.locatorEl.style.setProperty(
					'--eml-mobile-search-bottom',
					`${mobileSearchBottom}px`,
				);
			}
		};

		update();
		window.addEventListener('resize', update);
	};

	/**
	 * Get all relevent UI elements.
	 *
	 * @since 1.0.0
	 */
	getGlobalElements = () => {
		// Get search inputs from both containers
		const mainSearchEl = this.locatorEl.querySelector('.js-eml-search');
		const mapContainer = document.querySelector('.js-eml-map-container');
		const mapSearchEl = mapContainer
			? mapContainer.querySelector('.js-eml-search')
			: null;

		// Store both search inputs
		this.searchEls = [mainSearchEl, mapSearchEl].filter(Boolean);
		// Set the main search input as the default active one
		this.searchEl = mainSearchEl;

		// Update active search input when either input is focused
		this.searchEls.forEach((searchInput) => {
			if (searchInput) {
				searchInput.addEventListener('focus', () => {
					this.searchEl = searchInput;
				});
			}
		});

		const mainSearchBtns = this.locatorEl.querySelectorAll('.eml-search-btn');
		const mapSearchBtns = mapContainer
			? mapContainer.querySelectorAll('.eml-search-btn')
			: [];
		this.searchBtns = [...mainSearchBtns, ...mapSearchBtns];

		this.predictionsEl = this.locatorEl.querySelector('.js-eml-predictions');
		this.searchAlertEl = this.locatorEl.querySelector('.js-eml-search-alert');
		this.paginationEls = this.locatorEl.querySelectorAll('.js-eml-pagination');
		this.loadMoreButtons = this.locatorEl.querySelectorAll(
			'.js-eml-load-more-button',
		);
	};

	/**
	 * Initialize the Map layout.
	 *
	 * @since 1.1.0
	 */
	initMapLayout = () => {
		this.mapContainerEl = this.locatorEl.querySelector('.js-eml-map-container');
		if (this.isMapEnabled()) {
			this.mapLocationsEl = this.locatorEl.querySelector(
				'.js-eml-map-locations',
			);
			this.mapLocationDetailsEl = this.locatorEl.querySelector(
				'.js-eml-map-location-details',
			);
			this.mapBackButtonEl = this.locatorEl.querySelector(
				'.js-eml-map-back-button',
			);
			this.mapEl = this.locatorEl.querySelector('.js-eml-map');
			this.mapSidebarInnerEl = this.locatorEl.querySelector(
				'.eml-map__sidebar-inner',
			);
			this.mapLayoutControl = this.locatorEl.querySelector(
				'.js-eml-map-layout-control',
			);

			if (this.mapLayoutControl) {
				this.mapLayoutControl.addEventListener('click', this.showMapLayout);
			}

			this.mapEl.addEventListener(
				'wheel',
				(e) => {
					e.preventDefault();
					if (this.mapSidebarInnerEl) {
						this.mapSidebarInnerEl.scrollTop += e.deltaY;
					}
				},
				{ passive: false },
			);
		}
	};

	/**
	 * Show the map layout and hide the list layout.
	 *
	 * @since 1.0.0
	 */
	showMapLayout = () => {
		this.locatorEl.classList.add('show-map');
		this.locatorEl.classList.remove('show-list');
		this.map.fitBounds(this.map.bounds);
	};

	/**
	 * Initialize the List layout.
	 *
	 * @since 1.1.0
	 */
	initListLayout = () => {
		this.listLocationsEl = this.locatorEl.querySelector(
			'.js-eml-list-locations',
		);
		this.listLayoutControl = this.locatorEl.querySelector(
			'.js-eml-list-layout-control',
		);

		if (this.listLayoutControl) {
			this.listLayoutControl.addEventListener('click', this.showListLayout);
		}
	};

	/**
	 * Show the list layout and hide the map layout.
	 *
	 * @since 1.0.0
	 */
	showListLayout = () => {
		this.locatorEl.classList.remove('show-map');
		this.locatorEl.classList.add('show-list');
	};

	/**
	 * Update apiUrl with basic auth credientials, if available.
	 *
	 * @since 1.0.0
	 */
	initBasicAuth = () => {
		const { basicAuthUsername: user, basicAuthPassword: pass } = this.settings;
		if (user && pass) {
			this.fetchHeaders = {
				headers: {
					Authorization: `Basic ${btoa(`${user}:${pass}`)}`,
				},
			};
		} else {
			this.fetchHeaders = {};
		}
	};

	/**
	 * Check plugin settings to see which sharing methods are enabled,
	 * and setup booleans and event handlers.
	 *
	 * @since 1.0.0
	 */
	initShareMethods = () => {
		if (this.settings.shareMethods) {
			/**
			 * Extract share methods for easier use.
			 */
			const { download, email, print } = this.settings.shareMethods;

			/**
			 * Create booleans that tell which share methods are enabled for other functions to access.
			 */
			this.downloadEnabled = download !== undefined || false;
			this.emailEnabled = email !== undefined || false;
			this.printEnabled = print !== undefined || false;
			this.shareEnabled =				this.downloadEnabled || this.emailEnabled || this.printEnabled;

			/**
			 * Get all share controls and add event listeners.
			 */
			this.shareControls = this.locatorEl.querySelectorAll(
				'.js-eml-share-control',
			);
			forEach(this.shareControls, (i, control) => {
				control.addEventListener('click', this.handleShareControlClick);
			});

			if (this.emailEnabled) {
				this.emailFormContainer = this.locatorEl.querySelector(
					'.js-eml-email-form-container',
				);
				this.emailFormContainer.addEventListener(
					'click',
					this.handleEmailFormClose,
				);

				this.emailFormCloseButton = this.locatorEl.querySelector(
					'.js-eml-email-form-close-button',
				);
				this.emailFormCloseButton.addEventListener(
					'click',
					this.handleEmailFormClose,
				);

				this.emailForm = this.locatorEl.querySelector('.js-eml-email-form');
				this.emailForm.addEventListener('submit', this.handleEmailFormSubmit);

				this.locatorEl.addEventListener(
					'click',
					this.handleEmailFormControlClick,
				);

				this.emailFormAlert = this.locatorEl.querySelector(
					'.js-eml-email-form-alert',
				);
			}
		}
	};

	/**
	 * Initialize pagination functionality.
	 *
	 * @since 1.0.0
	 */
	initPagination = () => {
		forEach(this.loadMoreButtons, (i, loadMoreButton) => {
			if (!loadMoreButton.classList.contains('eml-load-cluster-locations')) {
				loadMoreButton.addEventListener('click', this.handleLoadMore);
			}
		});
	};

	/**
	 * Initialize Google Maps.
	 *
	 * @since 1.0.0
	 */
	initGoogleMaps = async () => {
		const options = {
			libraries: ['places'],
		};
		const loader = new Loader(this.settings.googleMapsApiKey, options);
		try {
			this.google = await loader.load();
			this.initSearch();
			this.getPhysicians();
			this.geolocate();
			if (this.isMapEnabled()) {
				this.initMap();
			}
		} catch (error) {
			console.error('initGoogleMaps: Error loading Google Maps API:', error);
		}
	};

	/**
	 * Initialize Google's AutocompleteService and Geocoder classes for later use,
	 * and register all search related event listeners.
	 *
	 * @since 1.0.0
	 */
	initSearch = () => {
		this.geocoder = new this.google.maps.Geocoder();

		// Add event listeners to all search inputs
		this.searchEls.forEach((searchInput) => {
			if (searchInput) {
				// Add input event listener to track value changes
				searchInput.addEventListener('input', (event) => {
					event.target.dataset.lastValue = event.target.value;
				});

				// Add focus/blur handlers
				searchInput.addEventListener('focusin', this.handleInputFocus);
				searchInput.addEventListener('blur', this.handleInputBlur);

				// Add keypress handler for Enter key
				searchInput.addEventListener('keypress', (event) => {
					if (event.key === 'Enter') {
						event.preventDefault();
						const container = event.target.closest(
							'.eml-search__field-container',
						);
						if (container) {
							const searchBtn = container.querySelector('.eml-search-btn');
							if (searchBtn) {
								this.handleLocationSearch({ target: searchBtn });
							}
						}
					}
				});
			}
		});

		// Add click handlers to search buttons
		forEach(this.searchBtns, (i, searchBtn) => {
			searchBtn.addEventListener('click', (event) => {
				event.preventDefault();
				event.stopPropagation();

				// Find the associated search input
				const container = searchBtn.closest('.eml-search__field-container');
				if (container) {
					const searchInput = container.querySelector('.js-eml-search');
					if (searchInput) {
						searchInput.dataset.lastValue = searchInput.value;
					}
				}

				this.handleLocationSearch(event);
			});
		});

		document.addEventListener('keydown', this.handleKeyboardNavigation);
		document.addEventListener('mousedown', this.handlePredictionClick);
		document.addEventListener('touchstart', this.handlePredictionClick);
	};

	/**
	 * Initialize the map.
	 *
	 * @since 1.0.0
	 */
	initMap = () => {
		const mapSettings = {
			zoom: parseInt(this.settings.zoomLevel),
			center: new this.google.maps.LatLng(
				this.settings.centerLat,
				this.settings.centerLng,
			),
			gestureHandling: 'none',
			mapTypeControlOptions: {
				position: this.google.maps.ControlPosition.BOTTOM_LEFT,
			},
			fullscreenControl: false,
		};

		if (this.settings.googleMapsTheme) {
			mapSettings.styles = JSON.parse(this.settings.googleMapsTheme);
		}

		this.map = new this.google.maps.Map(this.mapEl, mapSettings);

		this.updateMarkerIconSizes();

		this.markerIconHover = {
			url: this.markerIconHover.url,
			scaledSize: this.markerIcon.scaledSize,
			anchor: this.markerIcon.anchor,
		};

		if (window.innerWidth <= 768) {
			const mapContainer = document.querySelector(
				'.find-a-urologist__google-map-container',
			);
			const offset = 120;

			const handleScroll = () => {
				const rect = mapContainer.getBoundingClientRect();
				if (rect.top <= offset) {
					mapContainer.classList.add('is-fixed');
				} else {
					mapContainer.classList.remove('is-fixed');
				}
			};

			window.addEventListener('scroll', handleScroll);

			// Handle resize
			let resizeTimeout;
			window.addEventListener('resize', () => {
				clearTimeout(resizeTimeout);
				resizeTimeout = setTimeout(() => {
					if (window.innerWidth > 768) {
						mapContainer.classList.remove('is-fixed');
						window.removeEventListener('scroll', handleScroll);
					} else {
						window.addEventListener('scroll', handleScroll);
					}
				}, 250);
			});
		}

		this.google.maps.event.addListener(this.map, 'idle', () => {
			this.mapCenter = this.map.getCenter();
		});

		this.google.maps.event.addListener(window, 'resize', () => {
			this.google.maps.event.trigger(this.map, 'resize');
		});

		this.google.maps.event.addListener(this.map, 'resize', this.recenterMap);

		/**
		 * Setup map sidebar events.
		 */
		// this.mapLocationsEl.addEventListener('click', this.handleMapLocationClick);
		this.mapLocationsEl.addEventListener(
			'mouseover',
			this.handleMapLocationHover,
		);
		this.mapLocationsEl.addEventListener(
			'mouseout',
			this.handleMapLocationLeave,
		);
		this.mapLocationsEl.addEventListener('touchend', this.handleCardTouch);
		this.mapBackButtonEl.addEventListener('click', this.hideMapLocationDetails);

		this.mapEl.addEventListener('mouseover', (e) => {
			if (e.target.closest('.gm-style-iw-c')) {
				this.cancelHoverClose();
			}
		});

		this.mapEl.addEventListener('mouseout', (e) => {
			if (
				e.target.closest('.gm-style-iw-c')
				&& !e.relatedTarget?.closest('.gm-style-iw-c')
			) {
				this.scheduleHoverClose();
			}
		});
	};

	/**
	 * Use the Geolocation API to get the user's current location in order to
	 * center the map, and set the Autocomplete bounds. Position is stored in
	 * localStorage, so that on subsequent views, the map will instantly load
	 * in the right location.
	 *
	 * @since 1.0.0
	 */
	geolocate = () => {
		/**
		 * Make sure the user's browser supports geolocation before continuing.
		 */
		if (navigator.geolocation) {
			// Track changes to geolocation permission.
			navigator.permissions.query({ name: 'geolocation' }).then((result) => {
				if (result.state === 'prompt') {
					window.dataLayer.push({
						event: 'FauGeolocationPromptShown',
						prompt_event: 'location_prompt_shown',
					});
				}

				result.onchange = () => {
					let dataLayerEvent;

					// Add conditional checks for when user 1) hasn't interacted with the prompt, 2) has allowed geolocation, 3) has denied geolocation.
					if (result.state === 'prompt') {
						dataLayerEvent = {
							event: 'FauGeolocationPromptShown',
							prompt_event: 'location_prompt_shown',
						};
					} else if (result.state === 'granted') {
						dataLayerEvent = {
							event: 'FauGeolocationAllowed',
							prompt_event: 'location_allowed',
						};
					} else if (result.state === 'denied') {
						dataLayerEvent = {
							event: 'FauGeolocationDenied',
							prompt_event: 'location_denied',
						};
					}

					window.dataLayer.push(dataLayerEvent);
				};
			});

			if (localStorage.emlUserPosition) {
				/**
				 * Check if emlUserPosition is in localStorage, and if so,
				 * use that to show locations close to the user's location.
				 */
				this.userPosition = JSON.parse(localStorage.emlUserPosition);
				this.useCurrentPosition();
			} else {
				/**
				 * Add loading class and message to components.
				 */
				this.searchEl.value = 'Loading results near your location...';
				this.searchEl.setAttribute('disabled', 'disabled');
				this.locatorEl.classList.add('loading');

				/**
				 * Remove loading class and message from components.
				 */
				const stopLoading = () => {
					this.searchEl.value = '';
					this.searchEl.removeAttribute('disabled');
					this.locatorEl.classList.remove('loading');
				};

				/**
				 * Function to run when geolocation has run successfully.
				 * @param {Position} position
				 */
				const success = (position) => {
					stopLoading();

					/**
					 * Take only the values we need from the 'position' object
					 */
					this.userPosition = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
						accuracy: position.coords.accuracy,
					};

					this.currentPosition = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					};

					this.useCurrentPosition();

					/**
					 * Save the user's current position for later use.
					 */
					localStorage.setItem(
						'emlUserPosition',
						JSON.stringify(this.userPosition),
					);
				};

				/**
				 * Function to run when geolocation fails.
				 * @param {PositionError} error
				 */
				const error = () => {
					stopLoading();
				};

				navigator.geolocation.getCurrentPosition(success, error);
			}
		} else {
			console.warn('geolocate: Browser does not support geolocation');
		}
	};

	/**
	 * Update the map and results using the user's current location.
	 *
	 * @since 1.0.0
	 */
	useCurrentPosition = () => {
		this.getPhysicians(this.userPosition.lat, this.userPosition.lng);
		this.searchEl.value = 'Currently using your location. Click to change.';
		this.searchEl.classList.add('has-current-location-message');
	};

	/**
	 * Clear out the "current location" placeholder message from the input on focus.
	 *
	 * @since 1.0.0
	 */
	handleInputFocus = () => {
		this.searchEl.classList.add('focused');
		if (this.searchEl.classList.contains('has-current-location-message')) {
			this.searchEl.value = '';
			this.searchEl.classList.remove('has-current-location-message');
			this.searchAlertEl.innerHTML = '';
		}
	};

	/**
	 * Remove classes from the searchEl and predictionsEl when the searchEl loses focus.
	 *
	 * @since 1.0.0
	 */
	handleInputBlur = () => {
		if (this.hasResults()) {
			this.showNoResultsAlert();
		} else {
			this.searchAlertEl.innerHTML = '';
		}
		setTimeout(() => {
			this.searchEl.classList.remove('focused');
			this.predictionsEl.classList.remove('active');
		}, 150);
	};

	/**
	 * Check to see if a prediction from the autocomplete dropdown was clicked,
	 * and if so, trigger this.handlePredictionSelect(), passing the event target.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handlePredictionClick = (e) => {
		if (e.target.classList.contains('js-ac-prediction')) {
			this.handlePredictionSelect(e.target);
		}
	};

	/**
	 * Enabled keyboard navigation of the autocomplete dropdown. User's can use the up and down
	 * arrows to highlight the prediction, and then can hit 'enter' to select it. Hitting the
	 * 'escape' key will close the dropdown.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleKeyboardNavigation = (e) => {
		if (this.searchEl.classList.contains('focused')) {
			switch (e.key) {
				case 'ArrowDown':
				case 'ArrowUp': {
					e.preventDefault();
					const predictionEls =						this.predictionsEl.querySelectorAll('.js-ac-prediction');
					const activePredictionEl = this.predictionsEl.querySelector(
						'.js-ac-prediction.active',
					);

					let activeIndex = -1;
					forEach(predictionEls, (i, predictionEl) => {
						if (predictionEl === activePredictionEl) {
							activeIndex = i;
						}
					});

					/**
					 * Update activeIndex according to which arrow key was pressed.
					 */
					if (e.key === 'ArrowDown') {
						if (activeIndex === -1) {
							activeIndex = 0;
						} else if (activeIndex !== predictionEls.length - 1) {
							activeIndex += 1;
						}
					} else if (activeIndex > 0) {
						activeIndex -= 1;
					}

					/**
					 * Remove the active class from the previous prediction, and add it to the new prediction.
					 */
					activePredictionEl.classList.remove('active');
					predictionEls[activeIndex].classList.add('active');
					break;
				}

				case 'Enter': {
					this.handleLocationSearch();
					break;
				}

				case 'Escape': {
					this.searchEl.value = '';
					this.searchEl.blur();
					break;
				}
				default: {
					break;
				}
			}
		}
	};

	/**
	 * Trigger a call to this.getPhysicians() when a prediction from the autocomplete dropdown
	 * is selected. If the node has a lat and lng in its dataset, then make the request
	 * immediately, otherwise, use the place-id in its dataset to get the lat and lng
	 * using Google's Geocoder class.
	 *
	 * @param {Node} prediction
	 */
	handlePredictionSelect = (prediction) => {
		if (prediction) {
			this.searchEl.value = prediction.textContent;
			this.searchEl.blur();

			if (prediction.dataset.lat && prediction.dataset.lng) {
				/**
				 * If the node has a lat and lng in its dataset, then make the request immediately.
				 */
				this.getPhysicians(prediction.dataset.lat, prediction.dataset.lng);
			} else if (prediction.dataset.placeId) {
				/**
				 * Otherwise, use the place-id in its dataset to get the lat and lng using Google's
				 * Geocoder class.
				 */
				this.geocoder.geocode(
					{ placeId: prediction.dataset.placeId },
					(results, status) => {
						if (status === 'OK') {
							if (results[0]) {
								const { location } = results[0].geometry;
								this.getPhysicians(location.lat(), location.lng());
							} else {
								console.log('No results found');
							}
						}
					},
				);
			}
		}
	};

	/**
	 * Open the appropriate share template based on the share method of the control clicked.
	 *
	 * @since 1.0.0
	 */
	handleShareControlClick = (e) => {
		const control = e.target.closest('.js-eml-share-control');
		const shareMethod = control.dataset.share;
		const perPage = this.locations.length;
		const shareUrl = `${this.settings.archiveUrl}?lat=${this.currentPosition.lat}&lng=${this.currentPosition.lng}&share=${shareMethod}&per_page=${perPage}`;

		window.open(shareUrl, '_blank');
	};

	handleLocationSearch = (event) => {
		// If event is provided, find the associated search input
		let searchInput = this.searchEl;
		if (event && event.target) {
			const searchBtn = event.target.closest('.eml-search-btn');
			if (searchBtn) {
				const container = searchBtn.closest('.eml-search__field-container');
				if (container) {
					const containerSearchInput =						container.querySelector('.js-eml-search');
					if (containerSearchInput) {
						searchInput = containerSearchInput;
					}
				}
			}
		}

		// Get the search value, checking both the current value and the stored last value
		const currentValue = searchInput.value.trim();
		const lastValue = searchInput.dataset.lastValue
			? searchInput.dataset.lastValue.trim()
			: '';
		const searchValue = currentValue || lastValue;

		// Don't proceed if search value is empty
		if (!searchValue) {
			this.searchAlertEl.innerHTML = `<div class="alert alert--danger alert--inline">
				<span class="alert__content">Please enter a location to search.</span>
			</div>`;
			return;
		}

		// Clear any existing error messages
		this.searchAlertEl.innerHTML = '';

		this.geocoder.geocode({ address: searchValue }, (results, status) => {
			if (status === 'OK') {
				if (results[0]) {
					const { location } = results[0].geometry;
					this.getPhysicians(location.lat(), location.lng());
				} else {
					this.searchAlertEl.innerHTML = `<div class="alert alert--danger alert--inline">
						<span class="alert__content">No results found for "${searchValue}". Please try a different location.</span>
					</div>`;
				}
			} else {
				this.searchAlertEl.innerHTML = `<div class="alert alert--danger alert--inline">
					<span class="alert__content">Please enter a valid location to search.</span>
				</div>`;
			}
		});

		window.dataLayer.push({
			event: 'find_urologist_search',
			search_term: searchValue,
		});
	};

	/**
	 * Update the autocomplete search predictions using google's AutocompleteService and the
	 * FATC autocomplete api endpoint.
	 *
	 * @since 1.0.0
	 */
	updateAutocompletePredictions = () => {
		/**
		 * Add classes to searchEl and predictionsEl.
		 */
		this.searchEl.classList.add('focused');
		this.predictionsEl.classList.add('active');

		const searchValue = this.searchEl.value;

		/**
		 * Only proceed if the searchValue, when trimmed, doesn't match the previous search term.
		 * Eliminates extra requests when the user types a space.
		 */
		if (searchValue.trim() !== this.currentSearchValue) {
			/**
			 * Only proceed if the searchValue isn't an empty string to eliminate extra requests.
			 */
			if (searchValue !== '') {
				/**
				 * First, get predictions from the FATC autocomplete API. Then pass the predictions along,
				 * and get additional predictions from the Google AutocompleteService. Finally, pass all
				 * predictions on and build out the dropdown HTML.
				 *
				 * @type {Promise}
				 */
				const locatorPredictionsPromise = fetch(
					encodeURI(`${this.settings.apiUrl}?search=${searchValue}&per_page=3`),
					this.fetchHeaders,
				).then((response) => response.json());

				const autocompleteOptions = {
					input: searchValue,
					types: ['(regions)'],
				};

				if (this.settings.countryRestrictions.indexOf('none') === -1) {
					autocompleteOptions.componentRestrictions = {
						country: this.settings.countryRestrictions,
					};
				}

				const googlePredictionsPromise = new Promise((resolve) => {
					this.autocompleteService.getPlacePredictions(
						autocompleteOptions,
						(googlePredictions) => {
							resolve(googlePredictions || []);
						},
					);
				});

				Promise.all([locatorPredictionsPromise, googlePredictionsPromise])
					/**
					 * Merge Locator and Google predictions into one array and return it.
					 */
					.then((predictions) => predictions.reduce((acc, val) => acc.concat(val), []))
					.then((predictions) => {
						if (predictions.length > 0) {
							/**
							 * Placeholder for prediction dropdown HTML.
							 */
							let predictionsHtml = '';

							/**
							 * Loop through all predictions and add them to predictionsHtml.
							 */
							forEach(predictions, (i, prediction) => {
								let className = 'js-ac-prediction eml-dropdown-list__item';

								/**
								 * Add active class to the first prediction.
								 */
								if (i === 0) {
									className = `${className} active`;
								}

								if (prediction.lat && prediction.lng) {
									/**
									 * Handle locator predictions.
									 */
									predictionsHtml += `<li class="${className}" data-lat="${prediction.lat}" data-lng="${prediction.lng}">${prediction.post.post_title}</li>`;
								} else if (prediction.place_id) {
									/**
									 * Handle Google predictions.
									 */
									predictionsHtml += `<li class="${className}" data-place-id="${prediction.place_id}">${prediction.description}</li>`;
								}
							});

							/**
							 * Update the prediction dropdown's HTML.
							 */
							this.predictionsEl.innerHTML = predictionsHtml;
						} else {
							/**
							 * Add a helpful message if no results are found.
							 */
							this.predictionsEl.innerHTML =								'<div id="js-eml-no-results-item" class="eml-dropdown-list__item">No results found</div>';
						}
					})
					.catch(() => {
						/**
						 * Add a helpful message if no results are found.
						 */
						this.predictionsEl.innerHTML =							'<div id="js-eml-no-results-item" class="eml-dropdown-list__item">No results found</div>';
					});
			}

			/**
			 * Keep track of the new search value for later comparison.
			 */
			this.currentSearchValue = searchValue.trim();
		}
	};

	/**
	 * Determine if there are any valid search results.
	 *
	 * @since 1.0.0
	 */
	hasResults = () => {
		const noResultsItem = this.locatorEl.querySelector(
			'.js-eml-no-results-item',
		);
		if (noResultsItem) {
			return false;
		}

		const results = this.predictionsEl.querySelectorAll(
			'.eml-dropdown-list__item',
		);
		if (results.length === 0) {
			return false;
		}

		return true;
	};

	/**
	 * Show the placeholder message.
	 *
	 * @since 1.0.0
	 */
	showSearchInputLabel = () => {
		this.searchAlertEl.innerHTML = `<div class="alert alert--info alert--inline">
			<span class="alert__content">${this.settings.searchInputLabel}</span>
		</div>`;
	};

	/**
	 * Show the no results message.
	 *
	 * @since 1.0.0
	 */
	showNoResultsAlert = () => {
		this.searchAlertEl.innerHTML = `<div class="alert alert--info alert--inline">
			<span class="alert__content">${this.settings.noResultsMessage}</span>
		</div>`;
	};

	/**
	 * Recenter the map on the current center.
	 *
	 * @since 1.0.0
	 */
	recenterMap = () => {
		this.map.setCenter(this.mapCenter);
	};

	/**
	 * Make a request to the WP REST API endpoint using lat and lng to return the closest locations.
	 *
	 * @since 1.0.0
	 *
	 * @param {Number} lat Latitude
	 * @param {Number} lng Longitude
	 * @param {Number} limit Number of results to return with the request.
	 */
	getPhysicians = (lat = false, lng = false, page = 1) => {
		let apiUrl = this.settings.physiciansApiUrl;
		if (lat || lng || page) {
			apiUrl += '?';
			const params = [];

			if (lat) {
				params.push(`lat=${lat}`);
			}

			if (lng) {
				params.push(`lng=${lng}`);
			}

			if (page) {
				params.push(`page=${page}`);
			}

			if (params.length > 0) {
				apiUrl += params.join('&');
			}
		}

		const emlPreFetchLocations = new CustomEvent('emlPreFetchLocations', {
			detail: { apiUrl },
		});

		window.dispatchEvent(emlPreFetchLocations);

		fetch(emlPreFetchLocations.detail.apiUrl, this.fetchHeaders)
			.then((response) => {
				this.pagination.totalPosts = parseInt(
					response.headers.get('X-WP-Total'),
				);
				this.pagination.totalPages = parseInt(
					response.headers.get('X-WP-TotalPages'),
				);
				this.pagination.currentPage = page;

				forEach(this.paginationEls, (i, paginationEl) => {
					if (page < this.pagination.totalPages) {
						paginationEl.style.display = 'block';
					} else {
						paginationEl.style.display = 'none';
					}
				});
				return response.json();
			})
			.then((physicians) => {
				this.locations =					page === 1 ? physicians : this.locations.concat(physicians);
				this.currentPosition = { lat, lng };
				this.searchAlertEl.innerHTML = '';

				const emlPostFetchLocations = new CustomEvent('emlPostFetchLocations', {
					detail: {
						locations: this.locations,
						lat,
						lng,
						pagination: this.pagination,
					},
				});

				window.dispatchEvent(emlPostFetchLocations);

				this.handleNewLocations();

				if (this.emailEnabled) {
					this.emailForm.action = `${this.settings.archiveUrl}?lat=${lat}&lng=${lng}`;
				}

				forEach(this.loadMoreButtons, (i, loadMoreButton) => {
					loadMoreButton.removeAttribute('disabled');
				});
			});
	};

	/**
	 * Load more locations, if available, when the load more button is clicked.
	 */
	handleLoadMore = () => {
		if (this.pagination.currentPage < this.pagination.totalPages) {
			forEach(this.loadMoreButtons, (i, loadMoreButton) => {
				loadMoreButton.setAttribute('disabled', 'disabled');
			});

			// Store the current scroll position
			const currentScroll = window.pageYOffset;

			this.getPhysicians(
				this.currentPosition.lat,
				this.currentPosition.lng,
				this.pagination.currentPage + 1,
			).then(() => {
				// Wait for DOM to update
				requestAnimationFrame(() => {
					// Kill existing ScrollTrigger
					if (this.scrollTrigger) {
						this.scrollTrigger.kill();
					}

					// Create new ScrollTrigger
					this.initScrollTrigger();

					// Restore scroll position
					window.scrollTo(0, currentScroll);
				});
			});
		}
	};

	/**
	 * Open the email form when an email form control is clicked,
	 * and update the email form's "action" attribute in the process.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleEmailFormControlClick = (e) => {
		const control = e.target.closest('.js-eml-email-control');
		if (control) {
			this.locatorEl.classList.add('show-email-form');

			/**
			 * If this isn't a control inside of a location, reset the email form action
			 * back to the archive url to share the full list of locations.
			 */
			const location = control.closest('.eml-location');
			if (!location) {
				const { lat, lng } = this.currentPosition;
				this.emailForm.action = `${this.settings.archiveUrl}?lat=${lat}&lng=${lng}`;
			}
		}
	};

	/**
	 * Close the email form when clicking outside of it or hitting the escape key.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleEmailFormClose = (e) => {
		const { target } = e;
		if (
			this.emailFormContainer === target
			|| this.emailFormCloseButton === target
		) {
			this.closeEmailForm();
		}
	};

	/**
	 * Close the email form and reset any alerts.
	 *
	 * @since 1.0,0
	 */
	closeEmailForm = () => {
		this.locatorEl.classList.remove('show-email-form');
		this.resetEmailFormAlert();
	};

	/**
	 * Clear any alerts triggered by the email form.
	 *
	 * @since 1.0.0
	 */
	resetEmailFormAlert = () => {
		this.emailFormAlert.classList.remove('success');
		this.emailFormAlert.classList.remove('danger');
		this.emailFormAlert.innerHTML = '';
	};

	/**
	 * Function to run when the email form successfully submits.
	 *
	 * @since 1.0.0
	 */
	handleEmailFormSuccess = () => {
		this.emailFormAlert.classList.remove('loading');
		this.emailFormAlert.innerHTML = 'Email sent!';
	};

	/**
	 * Function to run when the email form fails to submit.
	 *
	 * @since 1.0.0
	 */
	handleEmailFormError = () => {
		this.emailFormAlert.classList.remove('success');
		this.emailFormAlert.classList.remove('loading');
		this.emailFormAlert.classList.add('danger');
		this.emailFormAlert.innerHTML = 'Email failed to send. Please try again.';
	};

	/**
	 * Use ajax to submit the form, which will trigger wp_mail() to send the email.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleEmailFormSubmit = (e) => {
		e.preventDefault();

		/**
		 * Reset any previous alerts triggered by the email form,
		 * and show loading animation.
		 */
		this.resetEmailFormAlert();
		this.emailFormAlert.classList.add('success');
		this.emailFormAlert.classList.add('loading');
		const startTime = new Date().getTime() / 1000;

		/**
		 * Build the url to open that will trigger wp_mail().
		 */
		const data = serialize(this.emailForm);
		let url = this.emailForm.action;
		url += this.emailForm.action.indexOf('?') > -1 ? '&' : '?';
		url += data;

		fetch(url, this.fetchHeaders)
			.then((response) => response.json())
			.then((json) => {
				if (json.sent) {
					const endTime = new Date().getTime() / 1000;
					const requestDuration = endTime - startTime;

					/**
					 * Check how long the request took, and if it was less than 2 seconds,
					 * add a delay so there is some percieved delay and it looks like something
					 * happened.
					 */
					if (requestDuration < 2) {
						setTimeout(() => {
							this.handleEmailFormSuccess();
						}, 2000);
					} else {
						this.handleEmailFormSuccess();
					}
				} else {
					this.handleEmailFormError();
				}
			});
	};

	/**
	 * Add location details HTML to the mapLocationDetailsEl, slide map into view and center on pin.
	 *
	 * @since 1.0.0
	 *
	 * @param {Number} index The index of the location in this.locations
	 */
	showMapLocationDetails = (index) => {
		/**
		 * Use the passed in index value to get the location, marker, and infowindow.
		 */
		const location = this.locations[index];
		const marker = this.markers[index];
		const infowindow = this.infowindows[index];

		// Safety check
		if (!location || !marker || !infowindow) {
			console.warn('Location, marker, or infowindow not found at index', index);
			return;
		}

		/**
		 * Update the email form action, if email sharing is enabled.
		 */
		if (this.emailEnabled) {
			this.emailForm.action = location.permalink;
		}

		/**
		 * Close any open infowindows.
		 */
		if (this.hoverInfowindow) {
			this.hoverInfowindow.close();
			this.hoverInfowindow = null;
		}
		if (this.activeInfowindow) {
			this.activeInfowindow.close();
		}

		/**
		 * Set marker to hover state to show it's selected
		 */
		marker.setIcon(this.markerIconHover);

		/**
		 * Open the target infowindow.
		 */
		infowindow.open(this.map, marker);

		/**
		 * Set the target infowindow as the activeInfowindow.
		 */
		this.activeInfowindow = infowindow;

		/**
		 * Only center map on marker if it's not currently visible or if there's not enough space for the info window.
		 */
		const markerPosition = marker.getPosition();
		const mapBounds = this.map.getBounds();
		const currentZoom = this.map.getZoom();

		// Check if marker is currently visible in the viewport
		const markerVisible = mapBounds && mapBounds.contains(markerPosition);

		// If marker is not visible, center on it
		if (!markerVisible) {
			this.map.setCenter(markerPosition);
		} else {
			// If marker is visible, check if there's enough space for info window
			// Get the marker's pixel position to check viewport edges
			const projection = this.map.getProjection();
			if (projection) {
				const markerPixel = projection.fromLatLngToPoint(markerPosition);
				const mapCenter = this.map.getCenter();
				const centerPixel = projection.fromLatLngToPoint(mapCenter);

				// Get map container dimensions
				const mapDiv = this.map.getDiv();
				const mapWidth = mapDiv.offsetWidth;
				const mapHeight = mapDiv.offsetHeight;

				// Calculate marker position relative to map center in pixels
				const scale = 2 ** this.map.getZoom();
				const markerX = (markerPixel.x - centerPixel.x) * scale + mapWidth / 2;
				const markerY = (markerPixel.y - centerPixel.y) * scale + mapHeight / 2;

				// Define minimum space needed for info window (approximate)
				const infoWindowHeight = 200; // pixels
				const infoWindowWidth = 250; // pixels
				const padding = 20; // pixels

				// Check if there's enough space above the marker for the info window
				const needsRepositioning =					markerY < infoWindowHeight + padding // Too close to top
					|| markerX < padding // Too close to left
					|| markerX > mapWidth - infoWindowWidth - padding // Too close to right
					|| markerY > mapHeight - padding; // Too close to bottom

				// Only center if repositioning is needed
				if (needsRepositioning) {
					this.map.setCenter(markerPosition);
				}
			}
		}

		// Don't change zoom if we're already at a reasonable level
		if (currentZoom < 12) {
			this.map.setZoom(12);
		}
	};

	/**
	 * Remove class that slides location details out of view.
	 *
	 * @since 1.0.0
	 */
	hideMapLocationDetails = () => {
		this.map.fitBounds(this.map.bounds);
		this.mapContainerEl.classList.remove('has-details');
		this.activeInfowindow.close();

		if (this.markers) {
			forEach(this.markers, (i, marker) => {
				marker.setIcon(this.markerIcon);
			});
		}

		/**
		 * Scroll the mapLocationDetailsEl to the top so it's posistioned properly when the next location
		 * is opened. Doing this on a timeout to avoid a flicker where the element jumps to
		 * the top before the slide transition can complete.
		 */
		setTimeout(() => {
			this.mapLocationDetailsEl.scrollTop = 0;
		}, 300);
	};

	/**
	 * Remove the current locations from the results list and their markers from the map.
	 *
	 * @since 1.0.0
	 */
	resetMap = () => {
		if (this.markers) {
			forEach(this.markers, (i, marker) => {
				marker.setMap(null);
				marker.setIcon(this.markerIcon);
			});

			this.markers = [];
		}

		/**
		 * Remove any added classes from the map container.
		 */
		this.mapContainerEl.classList.remove('has-details');
	};

	handleMapLocationClick = (e) => {
		if (e.target.classList.contains('eml-location__infolink')) {
			e.stopPropagation();
			const targetEl = e.target.closest('.eml-location');
			const locationEls = this.mapLocationsEl.querySelectorAll('.eml-location');

			forEach(locationEls, (i, locationEl) => {
				if (locationEl === targetEl) {
					this.markers[i].setIcon(this.markerIconHover);
				}
			});
		}
	};

	handleCardTouch = (e) => {
		const action = e.target.closest('.eml-physician-card__action');
		if (action) {
			e.preventDefault();
			window.location.href = action.href;
			return;
		}

		const body = e.target.closest('.eml-physician-card__body');
		if (body) {
			const card = body.closest('.eml-location');
			const locationEls = this.mapLocationsEl.querySelectorAll('.eml-location');
			forEach(locationEls, (i, locationEl) => {
				if (locationEl === card) {
					this.markers[i].setIcon(this.markerIconHover);
					this.showMapLocationDetails(i);
				}
			});
		}
	};

	openHoverInfowindow = (index) => {
		if (this.infowindows[index] === this.activeInfowindow) return;
		if (
			this.hoverInfowindow
			&& this.hoverInfowindow !== this.infowindows[index]
		) {
			this.hoverInfowindow.close();
		}
		this.hoverInfowindow = this.infowindows[index];
		this.hoverInfowindow.open(this.map, this.markers[index]);
	};

	scheduleHoverClose = () => {
		this.hoverCloseTimeout = setTimeout(() => {
			if (
				this.hoverInfowindow
				&& this.hoverInfowindow !== this.activeInfowindow
			) {
				this.hoverInfowindow.close();
				this.hoverInfowindow = null;
			}
		}, 150);
	};

	cancelHoverClose = () => {
		clearTimeout(this.hoverCloseTimeout);
		this.hoverCloseTimeout = null;
	};

	handleMapLocationHover = (e) => {
		e.stopPropagation();
		const targetEl = e.target.closest('.eml-location');
		const locationEls = this.mapLocationsEl.querySelectorAll('.eml-location');

		forEach(locationEls, (i, locationEl) => {
			if (locationEl === targetEl) {
				this.cancelHoverClose();
				this.markers[i].setIcon(this.markerIconHover);
				this.openHoverInfowindow(i);
			}
		});
	};

	handleMapLocationLeave = (e) => {
		e.stopPropagation();
		const targetEl = e.target.closest('.eml-location');
		const locationEls = this.mapLocationsEl.querySelectorAll('.eml-location');

		forEach(locationEls, (i, locationEl) => {
			if (locationEl === targetEl) {
				this.markers[i].setIcon(this.markerIcon);
				if (
					this.hoverInfowindow
					&& this.hoverInfowindow !== this.activeInfowindow
				) {
					this.scheduleHoverClose();
				}
			}
		});
	};

	/**
	 * Get the HTML markup for the no results alert message.
	 *
	 * @since 1.1.0
	 */
	getNoResultsAlert = () => `<li class="eml-no-results">
		<div class="eml-alert danger">${this.settings.noResultsMessage}</div>
	</li>`;

	hideClusterLocationDetails = (clusterBounds) => {
		// Close any active infowindow
		if (this.activeInfowindow) {
			this.activeInfowindow.close();
			this.activeInfowindow = null;
		}

		// Reset all markers to normal icon
		this.markers.forEach((marker) => {
			if (marker) {
				marker.setIcon(this.markerIcon);
			}
		});

		// Fit to cluster bounds and center
		this.map.fitBounds(clusterBounds);
		this.map.setCenter(clusterBounds.getCenter());
	};

	handleClusterLoadMore = () => {
		this.mapLocationsEl.innerHTML = '';
		let html = '';

		forEach(this.locations, (i, location) => {
			html += location.map_item_html;
		});

		this.mapLocationsEl.innerHTML = html;

		forEach(this.loadMoreButtons, (i, loadMoreButton) => {
			loadMoreButton.removeEventListener('click', this.handleClusterLoadMore);
			loadMoreButton.classList.remove('eml-load-cluster-locations');
			loadMoreButton.addEventListener('click', this.handleLoadMore);
			loadMoreButton.innerHTML = 'Load more';
		});

		this.map.setCenter(this.map.bounds.getCenter());
		this.map.fitBounds(this.map.bounds);
	};

	showClusterDetails = (cluster) => {
		const clusterBounds = cluster.bounds;
		const isMobile = window.innerWidth < 1025; // More standard mobile breakpoint
		const markerCount = cluster.markers.length;

		// First, fit the map to the bounds and center it
		this.map.fitBounds(clusterBounds);
		this.map.setCenter(clusterBounds.getCenter());

		// Adaptive zoom based on cluster size
		if (markerCount >= 10) {
			// Very large clusters (10+)
			const zoomLevel = isMobile
				? Math.min(this.map.getZoom() + 1, 14)
				: Math.min(this.map.getZoom() + 1, 13);
			this.map.setZoom(zoomLevel);
		} else if (markerCount >= 6) {
			// Medium clusters (6-9)
			const zoomLevel = isMobile
				? Math.min(this.map.getZoom() + 1, 15)
				: Math.min(this.map.getZoom() + 1, 14);
			this.map.setZoom(zoomLevel);
		} else {
			// Small clusters (2-5)
			const zoomLevel = isMobile
				? Math.min(this.map.getZoom() + 2, 16)
				: Math.min(this.map.getZoom() + 2, 15);
			this.map.setZoom(zoomLevel);
		}

		// Now update the map locations list with the markers from this cluster
		this.mapLocationsEl.innerHTML = '';
		let html = '';

		forEach(cluster.markers, (i, marker) => {
			const markerIndex = this.markers.indexOf(marker);
			if (markerIndex !== -1 && this.locations[markerIndex]) {
				html += this.locations[markerIndex].map_item_html;

				// Clear any existing cluster-specific click listeners on this marker
				this.google.maps.event.clearListeners(marker, 'click');

				// Add the original marker click event (for location details)
				this.google.maps.event.addListener(marker, 'click', () => {
					this.showMapLocationDetails(markerIndex);
				});
			}
		});

		this.mapLocationsEl.innerHTML = html;

		// Setup the load more buttons for this cluster area
		forEach(this.loadMoreButtons, (i, loadMoreButton) => {
			loadMoreButton.removeEventListener('click', this.handleLoadMore);
			loadMoreButton.classList.add('eml-load-cluster-locations');
			loadMoreButton.addEventListener('click', this.handleClusterLoadMore);
			loadMoreButton.innerHTML = 'Load more in this area';
		});
	};

	updateMarkerIconSizes = () => {
		const isMobile = window.matchMedia('(max-width: 768px)').matches;
		const markerW = isMobile ? 60 : 120;
		const markerH = isMobile ? 68 : 136;
		const anchorX = isMobile ? 30 : 60;
		const anchorY = isMobile ? 48 : 95;

		const scaledSize = new this.google.maps.Size(markerW, markerH);
		const anchor = new this.google.maps.Point(anchorX, anchorY);

		this.markerIcon = { url: this.markerIcon.url, scaledSize, anchor };
		this.markerIconHover = {
			url: this.markerIconHover.url,
			scaledSize,
			anchor,
		};
	};

	updateMapLocations = () => {
		/**
		 * Clear map markers and map classes before anything else.
		 */
		this.updateMarkerIconSizes();
		this.resetMap();

		/**
		 * Placeholder for storing html.
		 */
		let html = '';

		if (this.locations.length > 0) {
			/**
			 * Reset existing map bounds with new LatLngBounds object.
			 */
			this.map.bounds = new this.google.maps.LatLngBounds();

			forEach(this.locations, (i, location) => {
				/**
				 * Add marker for location. Physicians carry their location's lat/lng.
				 */
				this.markers[i] = new this.google.maps.Marker({
					position: new this.google.maps.LatLng(location.lat, location.lng),
					map: this.map,
					icon: this.markerIcon,
				});

				/**
				 * Extend the bounds to include the marker.
				 */
				this.map.bounds.extend(this.markers[i].getPosition());

				let infoWindowContent = '<div class="eml-infowindow">';

				infoWindowContent += '<header><address>';

				if (location.location_name) {
					infoWindowContent += `<span class="eml-infowindow__title">${location.location_name}</span>`;
				}

				if (location.location_formatted_address) {
					infoWindowContent += `<span class="eml-infowindow__address">${location.location_formatted_address}</span>`;
					const encodedAddress = encodeURIComponent(
						location.location_formatted_address,
					);
					const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
					const arrowIcon =						'<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true"><path d="M4.5 9.75H6V7.5H8.625V9.375L11.25 6.75L8.625 4.125V6H5.25C5.0375 6 4.85938 6.07188 4.71563 6.21563C4.57188 6.35938 4.5 6.5375 4.5 6.75V9.75ZM7.5 15C7.3125 15 7.12813 14.9625 6.94688 14.8875C6.76563 14.8125 6.6 14.7 6.45 14.55L0.45 8.55C0.3 8.4 0.1875 8.23438 0.1125 8.05313C0.0375 7.87188 0 7.6875 0 7.5C0 7.3125 0.0375 7.12813 0.1125 6.94688C0.1875 6.76563 0.3 6.6 0.45 6.45L6.45 0.45C6.6 0.3 6.76563 0.1875 6.94688 0.1125C7.12813 0.0375 7.3125 0 7.5 0C7.6875 0 7.87188 0.0375 8.05313 0.1125C8.23438 0.1875 8.4 0.3 8.55 0.45L14.55 6.45C14.7 6.6 14.8125 6.76563 14.8875 6.94688C14.9625 7.12813 15 7.3125 15 7.5C15 7.6875 14.9625 7.87188 14.8875 8.05313C14.8125 8.23438 14.7 8.4 14.55 8.55L8.55 14.55C8.4 14.7 8.23438 14.8125 8.05313 14.8875C7.87188 14.9625 7.6875 15 7.5 15Z" fill="#130F56"/></svg>';
					infoWindowContent += `<div><a class="js-eml-directions-link eml-infowindow__directions" href="${directionsUrl}" target="_blank" rel="noopener noreferrer" tabindex="0">Get Directions${arrowIcon}</a></div>`;
				}

				infoWindowContent += '</address></header>';

				infoWindowContent += '</div>';

				/**
				 * Setup infowindow.
				 */
				this.infowindows[i] = new this.google.maps.InfoWindow({
					content: infoWindowContent,
				});

				/**
				 * Setup event to load the location details when the marker is clicked.
				 */
				this.google.maps.event.addListener(this.markers[i], 'click', () => {
					this.showMapLocationDetails(i);
					const markerClickEvent = new CustomEvent('emlMarkerClick', {
						detail: {
							location,
							marker: this.markers[i],
							infowindow: this.infowindows[i],
						},
					});
					window.dispatchEvent(markerClickEvent);
				});

				/**
				 * Add event to reset marker icon when infowindow is closed (without zooming out).
				 */
				this.google.maps.event.addListener(
					this.infowindows[i],
					'closeclick',
					() => {
						this.markers[i].setIcon(this.markerIcon);
						this.activeInfowindow = null;
					},
				);

				this.google.maps.event.addListener(this.markers[i], 'mouseover', () => {
					this.cancelHoverClose();
					this.markers[i].setIcon(this.markerIconHover);
					this.openHoverInfowindow(i);
				});

				this.google.maps.event.addListener(this.markers[i], 'mouseout', () => {
					this.markers[i].setIcon(this.markerIcon);
					if (
						this.hoverInfowindow
						&& this.hoverInfowindow !== this.activeInfowindow
					) {
						this.scheduleHoverClose();
					}
				});

				/**
				 * Build location list item html.
				 */
				html += location.map_item_html;
			});

			/**
			 * Make sure all the markers fit within the bounds of the map.
			 */
			this.map.setCenter(this.map.bounds.getCenter());
			this.map.fitBounds(this.map.bounds);

			if (!this.locatorEl.classList.contains('has-results')) {
				this.locatorEl.classList.add('has-results');
			}

			if (this.cluster) {
				this.cluster.clearMarkers();
				this.cluster = false;
			}
		} else {
			html = this.getNoResultsAlert();
		}

		/**
		 * Update the mapLocationsEl html with the new html.
		 */
		this.mapLocationsEl.innerHTML = html;
	};

	updateListLocations = () => {
		let html = '';

		if (this.locations.length > 0) {
			forEach(this.locations, (i, location) => {
				html += location.list_item_html;
			});
		} else {
			html = this.getNoResultsAlert();
		}

		this.listLocationsEl.innerHTML = html;
	};

	/**
	 * Use the results returned from the REST API to render the closest
	 * locations to the map, removing previously displayed locations.
	 *
	 * @since 1.0.0
	 */
	handleNewLocations = () => {
		if (this.isMapEnabled()) {
			this.updateMapLocations();
		}

		if (this.isListEnabled()) {
			this.updateListLocations();
		}

		if (this.mapSidebarInnerEl) {
			this.mapSidebarInnerEl.scrollTop = 0;
		}

		if (window.innerWidth < 768 && this.mapLocationsEl) {
			window.scrollTo({
				top: this.mapLocationsEl.offsetTop,
				behavior: 'instant',
			});
		}
	};

	handleResetClick = (e) => {
		e.preventDefault();
		this.resetMap();
	};

	/**
	 * Helper function to check if the map layout is enabled.
	 *
	 * @since 1.1.0
	 */
	isMapEnabled = () => !!this.mapContainerEl;

	/**
	 * Helper function to check if the list layout is enabled.
	 *
	 * @since 1.1.0
	 */
	isListEnabled = () => !!this.listLocationsEl;

	/**
	 * Get the current latitude coordinate.
	 *
	 * @since 1.1.0
	 */
	getLat = () => {
		if (
			this.currentPosition.lat
			|| (this.userPosition && this.userPosition.lat)
		) {
			return this.currentPosition.lat
				? this.currentPosition.lat
				: this.userPosition.lat;
		}
		return false;
	};

	/**
	 * Get the current longitude coordinate.
	 *
	 * @since 1.1.0
	 */
	getLng = () => {
		if (
			this.currentPosition.lng
			|| (this.userPosition && this.userPosition.lng)
		) {
			return this.currentPosition.lng
				? this.currentPosition.lng
				: this.userPosition.lng;
		}
		return false;
	};
}
